import localforage from "localforage";
import pWaitFor from "p-wait-for";
import {EntityType} from "../@types/entity";
import {uuid} from '../@types/schema';
import {captureException} from "../lib/picos/error";

export type StorageWorker = {
    updateStorage: typeof updateStorage;
    restore: typeof restore;
    processCached: typeof processCached;
    cacheItem: typeof cacheItem;
    uncacheItem: typeof uncacheItem;
    uncacheDependents: typeof uncacheDependents;
}

export type RestoreResult = {
    entityName: string,
    error: string
};

let backups: LocalForage;
let cache: LocalForage;

/**
 * Should we do excessive logging?
 * NOTE: You cannot access localStorage from within a web worker, so we have to hardcode here.
 */
let logging = false;

/**
 * What color should we use for logging statements?
 */
let logColor = 'background: purple;color: white';

// Mutex
let numProcessing = 0;

// Limit to 2 CPU cores at a time so that we do not overburden the CPU or memory
const MAX_PROCESSORS = 2;

/**
 * Get an instance to localForage pointing to the backups
 *
 * @param {uuid} firmId
 * @param {uuid} userId
 * @returns {LocalForage}
 */
function getBackups(firmId: uuid, userId: uuid): LocalForage {
    return backups ?? localforage.createInstance({
        name: `Backups_${firmId}_${userId}`,
        driver: localforage.INDEXEDDB,
        storeName: `Backups_${firmId}_${userId}`,
        description: `Redux backups for ${firmId}/${userId}`
    });
}

/**
 * Get an instance to localForage pointing to the cache
 *
 * @param {uuid} firmId
 * @param {uuid} userId
 * @returns {LocalForage}
 */
function getAutosaveCache(firmId: uuid, userId: uuid): LocalForage {
    if (!cache) cache = localforage.createInstance(
        {
            name: `Autosave_${firmId}_${userId}`,
            driver: localforage.INDEXEDDB,
            description: `Autosave entities for ${firmId}/${userId}`,
            storeName: `Autosave_${firmId}_${userId}`
        });

    return cache;
}

/**
 * Wait for a processor to open up.
 *
 * @param {string} functionName - name of the calling function
 * @param {string} type - entity type
 * @returns {Promise<void>}
 */
async function waitForProcessor(functionName: string, type: string): Promise<void> {
    if (numProcessing >= MAX_PROCESSORS) {
        if (logging) console.debug(`%cstorage: waiting for processor to free up, ${numProcessing} of ${MAX_PROCESSORS} processors in use`, logColor);

        // Wait for the mutex to clear
        try {
            await pWaitFor(() => numProcessing < MAX_PROCESSORS, {
                interval: 50, // check 20 times per second
                timeout: 120_000 // 2 minutes
            });

            numProcessing++;
        }
        catch (err: any) {
            // Timed out.
            if (logging) console.error(`\cstorage: Timed out waiting to process ${functionName} ${type}. ${err.message}`, logColor);
        }
    }
    else numProcessing++;
}

/**
 * Update storage with new entries.
 *
 * @param {uuid} firmId - used in conjunction with the userId to get the name of the database
 * @param {uuid} userId - used in conjunction with the firmId to get the name of the database
 * @param {string} entityType - the type of the objects in the storage/updates (e.g. task)
 * @param {string} store - the name of the store, e.g. tasks
 * @param {<T>[]} data - if present, the entire data set to store
 * @param {<T>[]} [updates] - if present, a list of objects to update. If this is present, data will be null.
 * @return {Promise<string>}
 */
export async function updateStorage<T extends EntityType>(
    firmId: uuid,
    userId: uuid,
    entityType: string,
    store: string,
    data: T[] | null,
    updates?: T[]
): Promise<string> {
    if (logging) console.debug(`%cstorage: updateStorage for ${entityType} with ${data?.length || 0} data and ${updates?.length || 0} updates`, logColor)
    await waitForProcessor('updateStorage', entityType);

    try {
        // There are issues storing empty arrays, so if there are no entries, we store null
        // (Will show up as undefined in Application/IndexedDB/Backups).
        // TODO: IS THIS STILL TRUE? Setting to an empty array here...
        if (logging && store === 'tasks' && !data && updates?.length === 0) {
            console.debug(`%cstorage: updateStorage no data and no updates, setting ${store} to []`, logColor);
            await getBackups(firmId, userId).setItem(store, []);
        }
        else {
            // There is data and/or updates to be processed.
            const backups = getBackups(firmId, userId);

            // If data was sent, we replace the data.
            // TODO: What if there were also updates? Is that possible?
            if (data) {
                if (logging) console.debug(`%cstorage: updateStorage data sent, replacing ${store} with ${data.length} ${store}.`, logColor);
                if (logging && updates?.length > 0) console.warn('%c' + `updateStorage${updates.length} updates are being ignored!, 'color: red;'`)
                await backups.setItem(store, data);
            }
            else {
                if (logging) console.debug(`%cstorage: updateStorage no data but ${updates.length} updates`, `color: ${logColor};`);
                const deletedEntryTypes = ['user', 'contact', 'activityType', 'activityCategory'];

                /**
                 * Get the store for the entity's table
                 *
                 * @type {Array<object>}
                 */
                const items: T[] = await backups.getItem(store) || [];
                if (logging) console.debug(`%cstorage:  updateStorage found ${items.length} items in ${store}`, logColor);

                /*
                 * Changed 2025-01-22, version 6.0.1 and 6.0.0-beta20-hf1, JCM
                 *
                 * Removed _ from `_${entityType}Id`, which was causing this key not to be found!
                 */
                // This is the id for the entity type, e.g. taskId
                const fieldName: keyof T = `${entityType}Id` as keyof T;

                if (logging && updates) console.debug(`%cstorage: updateStorage found ${updates} updates`, logColor);

                // Update each item, based on what is in the current storage
                updates?.forEach(item => {
                    // Find the existing index in the store using its entityID
                    const updateIndex = items.findIndex(e => e[fieldName] === item[fieldName]);

                    // Found it!
                    if (updateIndex !== -1) {
                        if (logging) console.debug('%cstorage: processUpdate found update in the storage', logColor);
                        // Handle delete
                        if (item.status === 'D') {
                            if (logging) console.debug('%cstorage: processUpdate update is marked as deleted in the store', logColor);
                            if (deletedEntryTypes.includes(entityType)) {
                                // Update the entry only (with the deleted one)
                                if (logging) console.debug('%cstorage: processUpdate replacing entry with the deleted one', logColor);
                                items.splice(updateIndex, 1, item);
                            }
                            else {
                                // Delete the entry
                                if (logging) console.debug('%cstorage: processUpdate deleting entry', logColor);
                                items.splice(updateIndex, 1);
                            }
                        }
                        else {
                            // Replace wholesale
                            if (logging) console.debug('%cstorage: processUpdate replacing a single task', logColor, fieldName, updateIndex, item['title']);
                            items.splice(updateIndex, 1, item);
                        }
                    }
                    // Not found
                    else {
                        if (logging) console.debug('%cstorage: %cprocessUpdate did not find update in the storage', logColor, 'color: orange');
                        // Ignore deletes
                        if (item.status !== 'D') {
                            // Add the item to the store
                            if (logging) console.debug('%cstorage: processUpdate but ignoring because status is is not deleted', logColor);
                            items.push(item);
                        }
                    }
                });

                // Now update the store
                if (logging) console.debug(`%cstorage: processUpdate update tasks with items`, logColor, items);
                await backups.setItem(store, items);
            }
        }

        return `Stored ${data?.length || updates?.length} item(s) to ${store}`;
    }
    catch (err: any) {
        captureException(err, {tags: ['storage.worker'], extra: {}});
        return err;
    }
    finally {
        numProcessing--;
    }
}

/**
 * Look in backups for the given entity, and return the results if found.
 * Otherwise, return an error object.
 *
 * @param {uuid} firmId
 * @param {uuid} userId
 * @param {string} entityName
 * @param {string} entityPluralName
 * @param {function} fetchSuccess
 * @param {function} fetchFailure
 */
export async function restore(
    firmId: uuid,
    userId: uuid,
    entityName: string,
    entityPluralName: string,
    fetchSuccess: Function,
    fetchFailure: Function
): Promise<RestoreResult> {
    const result = {
        entityName,
        error: ''
    };

    try {
        await waitForProcessor('restore', entityPluralName);

        if (logging) console.debug(`%cstorage: get backups for ${entityPluralName}`, logColor);

        const results = await getBackups(firmId, userId).getItem(entityPluralName);

        if (results) {
            // Let redux know we have results
            if (logging) console.debug(`%cstorage: calling fetchSuccess for ${entityPluralName}`, logColor);

            fetchSuccess(results);

        }
        else {
            if (logging) console.debug(`%cstorage: could not find backups for ${entityPluralName}`, logColor);
            result.error = `No storage found for ${entityPluralName}`;
        }

        return result;
    }
    catch (error: any) {
        result.error = `Could not load store for ${entityName}: ${error.message}`;

        // We don't want to try to use these results again
        try {
            await getBackups(firmId, userId).setItem(entityPluralName, '');
        }
        catch (err: any) {
            result.error = err.message + '; ' + result.error;
        }

        // Let redux know we didn't reload the cache
        fetchFailure(error);

        return result;
    }
    finally {
        numProcessing--;
    }
}

/**
 * Process cached items matching the parentId with a custom processor callback
 *
 * @param {uuid} firmId
 * @param {uuid} userId
 * @param {string} entityType
 * @param {uuid} parentId - the UUID of the parent entity
 * @param {function} processFn - callback to process any found items
 * @returns {Promise<void>}
 */
export async function processCached(
    firmId: uuid,
    userId: uuid,
    entityType: string,
    parentId: uuid,
    processFn: Function
): Promise<void> {
    try {
        await waitForProcessor('processCached', entityType);

        const keys = Array.from(await getAutosaveCache(firmId, userId).keys())
            .filter(key => key.startsWith(`${entityType} ${parentId}`));

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const item = await getAutosaveCache(firmId, userId).getItem(key);

            if (typeof (processFn) === 'function') await processFn(item);
        }
    }
    catch (err) {
        console.error(err);
    }
    finally {
        numProcessing--;
    }
}

/**
 * Cache the item
 *
 * @param {uuid} firmId
 * @param {uuid} userId
 * @param {string} entityType
 * @param {uuid} parentId
 * @param {uuid} id - the id of the entity
 * @param {<T extends EntityType>} entity - what you want to cache
 * @returns {Promise<void>}
 */
export async function cacheItem<T extends EntityType>(
    firmId: uuid,
    userId: uuid,
    entityType: string,
    parentId: uuid,
    id: uuid,
    entity: T
): Promise<void> {
    try {
        await waitForProcessor('cacheItem', entityType);

        const key = `${entityType} ${parentId} ${id}`;
        const {waitingToSave} = entity;

        if (logging) console.debug(`%cstorage: cacheItem bf getCache${waitingToSave ? 'waiting' : ''} ${key}`, 'color: brown');

        await getAutosaveCache(firmId, userId).setItem(key, entity);

        if (logging) console.debug(`%cstorage: cacheItem af getCache ${waitingToSave ? 'waiting' : ''} ${key}`, logColor);
    }
    catch (err) {
        console.error(err);
    }
    finally {
        numProcessing--;
    }
}

/**
 * Delete item from cache.
 *
 * @param {uuid} firmId
 * @param {uuid} userId
 * @param {string} entityType
 * @param {uuid} parentId
 * @param {uuid} id
 * @returns {Promise<void>}
 */
export async function uncacheItem(
    firmId: uuid | null,
    userId: uuid | null,
    entityType: string,
    parentId: uuid,
    id: uuid
): Promise<void> {
    if (!firmId || !userId) {
        console.error(`uncacheItem: firmId or userId is missing: ${firmId}, ${userId}`);
        return;
    }
    try {
        await waitForProcessor('uncacheItem', entityType);

        if (logging) console.debug(`%cstorage: uncacheItem ${entityType} ${parentId} ${id}`, logColor);

        // if (logging) console.debug('%cstorage: uncacheItem bf getCache ${entityType}', logColor);
        // Check if there is anything to remove
        const key = `${entityType} ${parentId} ${id}`;

        await getAutosaveCache(firmId, userId).removeItem(key);
    }
    catch (err) {
        console.error(err);
    }
    finally {
        numProcessing--;
    }
}

/**
 * Remove any cached items dependent on this item
 *
 * @param {uuid} firmId
 * @param {uuid} userId
 * @param {string} entityType
 * @param {uuid} entityId
 * @param {uuid} parentId
 * @returns {Promise<void>}
 */
export async function uncacheDependents(
    firmId: uuid,
    userId: uuid,
    entityType: string,
    parentId: uuid,
    entityId: uuid
): Promise<void> {
    // We need to find keys of the form <* entityId *>
    try {
        await waitForProcessor('uncacheDependents', entityType);

        if (logging) console.debug(`%cstorage: uncacheDependents bf getCache for ${entityType} ${entityId}`, logColor);

        const _cache = Array.from(await getAutosaveCache(firmId, userId).keys())
            .filter(key => key.split(' ')[1] === entityId);

        for (const key of _cache) {
            const [entityType, parentId, id] = key.split(' ');
            await uncacheItem(firmId, userId, entityType, parentId, id);
        }

        if (logging) console.debug(`%cstorage: uncacheDependents af getCache for ${entityType} ${entityId}`, logColor);

        await uncacheItem(firmId, userId, entityType, parentId, entityId);
    }
    catch (err) {
        console.error(err);
    }
    finally {
        numProcessing--;
    }
}
